import { memoize } from 'lodash-es';
import * as React from 'react';
import DateHelper from '../../DateHelper';
import DateButton from '../DateButton';
import { IDateObject } from '../../types';
import { pureUiTheme } from 'pureUi/pureUiTheme';
import styled from 'styled-components';
import { formatDate } from 'utils';
import { isNil } from 'lodash-es';
import { parseAsUTC } from 'utils/date.utc';

// Optimisation Warning: Memoized functions
// date.toLocaleString is used for for the following functions. But it's quite slow,
// so we memoize some functions to minimize those calls.
const resolver = (...args) => {
  const key = args.reduce((prev, next) => `${prev}-${next}`);
  return key;
};

const memoizedWeekdayHeadings = memoize(DateHelper.weekdayHeadingsWithLocale, resolver);
const memoizedGenerateDatesFor = memoize(DateHelper.generateDatesFor, resolver);

export interface ICalendarProps extends React.HTMLProps<HTMLDivElement> {
  locale?: string;
  localeDates?: string; // separate locale formatting for dates
  firstDayOfWeek?: number;
  currentDate: string; // An ISO8601 Date string
  selectedDates?: string[]; // An array of ISO8601 Date strings
  onDayClick?: React.EventHandler<any>;
  onDayMouseOver?: React.EventHandler<any>;
  disablePastDates: boolean;
  minDate?: string; // An ISO8601 Date string
  maxDate?: string; // An ISO8601 Date string
  disableDates?: string[]; // An array of ISO8601 Date strings
  enabledDates?: string[]; // An array of ISO8601 Date strings
  readonly?: boolean;

  // @see https://pureescapes.atlassian.net/browse/OWA-3938
  availableDates?: string[];
  unavailableDates?: string[];
  requestDates?: string[];
}

// -----------------------------------------------------------------------------

const normalizeDate = (date: Date | string | number): string => new Date(date).toISOString();

class Calendar extends React.Component<ICalendarProps, {}> {
  private currentMonth: number;
  private today = normalizeDate(new Date().setHours(0, 0, 0, 0));
  private localeOptions = { year: 'numeric', month: 'long', timeZone: 'GMT' };

  public shouldComponentUpdate(nextProps, nextState) {
    return nextProps !== this.props;
  }

  public static defaultProps: ICalendarProps = {
    currentDate: new Date().toISOString(),
    firstDayOfWeek: 0,
    disablePastDates: true,
    disableDates: [],
    enabledDates: undefined,
    availableDates: [],
    unavailableDates: [],
    requestDates: [],
  };

  private renderWeek = (week: IDateObject[]): React.ReactElement => {
    const key = `week-staring-${week[0].month}-${week[0].date}`;
    return (
      <div key={key} className="calendarWeek">
        {week.map(date => this.renderDate(date))}
      </div>
    );
  };

  private renderDate = (date: IDateObject): React.ReactElement => {
    const key = `${this.currentMonth}-date-${date.month}-${date.date}`;
    const isFirstDate = this.props.selectedDates && this.props.selectedDates[0] === date.dateString;

    // faster loop here - check dates in both broken iso format, and UTC
    // (iso for backwards compatibility)
    let isSelected = false;
    if (this.props.selectedDates) {
      for (let i = 0; i < this.props.selectedDates.length; i++) {
        if (
          this.props.selectedDates[i] === date.dateString ||
          this.props.selectedDates[i] === parseAsUTC(date.dateString)
        ) {
          isSelected = true;
          break;
        }
      }
    }

    const isDisabledPastDate = this.props.disablePastDates && date.dateString < this.today;
    const isBeforeMin = Boolean(this.props.minDate && date.dateString < normalizeDate(this.props.minDate));
    const isAfterMax = Boolean(this.props.maxDate && date.dateString > normalizeDate(this.props.maxDate));
    const isInListOfDisabledStrings = this.props.disableDates?.includes(date.dateString.split('T')[0]);
    const areEnabledDates = !isNil(this.props.enabledDates);
    const isInListOfEnabledStrings = this.props.enabledDates?.includes(date.dateString.split('T')[0]);

    const isDisabled =
      isDisabledPastDate ||
      isBeforeMin ||
      isAfterMax ||
      isInListOfDisabledStrings ||
      (areEnabledDates && !isInListOfEnabledStrings);

    const formattedDate = formatDate(date.dateString);
    let extraClassNames: string[] = [];

    if (date.month === this.currentMonth) {
      if (this.props.availableDates && this.props.availableDates.includes(formattedDate)) {
        extraClassNames.push('available');
      } else if (this.props.unavailableDates && this.props.unavailableDates.includes(formattedDate)) {
        extraClassNames.push('unavailable');
      } else if (this.props.requestDates && this.props.requestDates.includes(formattedDate)) {
        extraClassNames.push('request');
      }
    }

    return (
      <DateButton
        key={key}
        dateObject={date}
        className={`dateButton ${extraClassNames.join(' ')}`}
        onClick={this.props.onDayClick}
        onMouseOver={this.props.onDayMouseOver}
        isSelected={isSelected}
        isFirstDate={isFirstDate}
        isExtra={date.month !== this.currentMonth}
        isToday={this.today === date.dateString}
        isDisabled={isDisabled}
        readonly={this.props.readonly}
      />
    );
  };

  public render() {
    const dateString = this.props.currentDate;
    const date = new Date(dateString);
    this.currentMonth = date.getUTCMonth();

    return (
      <div className={this.props.className}>
        <header className="header">
          {/* @ts-ignore legacy */}
          <p className="month">{date.toLocaleString(this.props.locale, this.localeOptions)}</p>
        </header>
        <section className="calendarGrid">
          <div className="days">
            {memoizedWeekdayHeadings(this.props.locale ?? 'en-GB', this.props.firstDayOfWeek ?? 1).map((d, i) => (
              <span key={`wd-${d}${i}`} className="day">
                {d}
              </span>
            ))}
          </div>
          {memoizedGenerateDatesFor
            .call(
              DateHelper,
              this.currentMonth,
              date.getFullYear(),
              this.props.firstDayOfWeek,
              this.props.localeDates || this.props.locale
            )
            .map(week => this.renderWeek(week))}
        </section>
      </div>
    );
  }
}

// @ts-ignore
const StyledCalendar = styled(Calendar)`
  display: flex;
  flex-direction: column;
  user-select: none;
  color: ${pureUiTheme.colors.black};
  text-transform: uppercase;
  background-color: ${pureUiTheme.colors.white};

  .header {
    margin-bottom: 3px;
  }

  .calendarGrid {
    margin: 5px;
    width: 259px;
  }

  & .calendarWeek {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    text-align: center;
    margin-top: 5px;
  }

  & .month {
    font-weight: 600;
    font-size: 12px;
    background-color: ${pureUiTheme.colors.grayLight};
    margin: 0;
    display: flex;
    height: 48px;
    align-items: center;
    justify-content: center;
    letter-spacing: 0.7px;
  }

  & .days {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    text-align: center;
    font-size: 12px;
    font-weight: 600;
    line-height: 24px;
  }

  & .dateButton.available {
    background: #d4f3ee;
  }
  & .dateButton.unavailable {
    background: #fcecec;
  }
  & .dateButton.request {
    background: #ffeeb2;
  }
`;

export default StyledCalendar;
